import React, { useRef, useEffect, useCallback, useState} from 'react';
import OutputNode from './OutputNode.js';
import MiniTrackComponent from "components/v2/MiniTrackComponent.js";
import {useWindowManager} from "components/windows/useWindowManager.js";
import {NW, NE, SW, SE} from 'components/windows/resize-types.js';
import {connect} from 'react-redux';
import {
    setEffectStarred,
    setSampleTagDragging,
    setTrackDragging,
    setSampleDragging} from 'actions/index.js';

const PulledSequencer = ({
    sequencer,
    scale,
    pad,
    scrollContainer,
    elements,
    sizeRef,
    size,
    setSize,
    isCurrent,
    setTrackDragging,
    numTracks,
    setOrdering,
    isStarred,
    setStarred,
    coords,
    ordering,
    setSampleDragging,
    setSampleTagDragging,
    isSelected
}) => { 
    const ref = useRef(undefined);
    const [trackPositions, setTrackPositions] = useState({});
    const [isHiding, setIsHiding] = useState(false);
    const scrollRef = useRef(undefined);
    const windowWidth = useRef(undefined);

    const onTrackRendered = useCallback((track, ref) => {
        let _trackPositions = {... trackPositions};
        _trackPositions[track.id] = ref;
        setTrackPositions(_trackPositions);
    }, [trackPositions, setTrackPositions]);

    const {
        setWindowScale,
        style,
        adjust,
        windowScale,
        onContextMenu,
        onMouseDown,
        onMouseUp,
        setCoords,
        startResizing} = !elements ? {} : useWindowManager({
        scale,
        ref,
        sizeRef,
        sequencerEngine: sequencer.sequencerEngine,
        windowPosition: coords,
        containerRef: scrollContainer,
        size,
        setSize,
        elements,
        element: sequencer,
        setOrdering,
        ordering
    });

    useEffect(() => {
        if (coords) {
            sequencer.x = coords.x;
            sequencer.y = coords.y;
        }
    }, [coords]);

    useEffect(() => {
        if (!elements) {
            return;
        }
        // tracks changed so make sure it fits the size
        setTimeout(() => {
            if (!scrollRef.current) {
                return;
            }
            if (scrollRef.current.clientHeight < scrollRef.current.scrollHeight) {
                let size  = {
                    x: windowWidth.current,
                    y: scrollRef.current.scrollHeight + 30
                };
                setWindowScale(size);
            } else {
                adjust(sequencer.x, sequencer.y);
            }
        }, 50);
    }, [numTracks, setWindowScale]);

    useEffect(() => {
        if (windowScale) {
            windowWidth.current = windowScale.x;
        }
    }, [windowScale]);

    const onDrop = useCallback((e) => {
        e.stopPropagation();
        sequencer.sequencerEngine.switchToSubSequence(
            sequencer.subSequence.id);
        let state = sequencer.sequencerEngine.state;
        let {trackDragging} = state.tracks;
        let {sampleDragging, sampleTagDragging} = state.selector;
        let {subSequence, sequencerEngine} = sequencer;
        if (trackDragging || sampleDragging || sampleTagDragging) {
            if (subSequence.pads.length === 0) {
                // subSequence.createPad();
            }
        }

        if (trackDragging) {
            // see if track exists in other pad if so delete from there
            // first
            for (let _sequencer of sequencer.subSequence.sequencers) {
                if (_sequencer === sequencer) { continue; }
                if (_sequencer.tracks.includes(trackDragging)) {
                    _sequencer.removeTrack(trackDragging);
                    break;
                }
            }
            sequencer.addTrack(trackDragging);
        } else if (sampleDragging) {
            sequencerEngine.addSound(sampleDragging)
                .then(x => sequencer.addTrack(x));
        } else if (sampleTagDragging) {
            let tags = sequencerEngine.state.selector.sampleTagDragging;
            if (!Array.isArray(tags)) {
                tags = [tags];
            }
            sequencerEngine.searchForSounds(
                tags,
                undefined,
                undefined,
                undefined,
                true,
            ).then(x => sequencer.addTrack(x));
        }
        setTrackDragging(undefined);
        setSampleDragging(undefined);
        setSampleTagDragging(undefined);
    }, []);

    let _style = {... style};
    if (windowScale && windowScale.x !== 0) {
        if (isHiding) {
            _style.minWidth = windowScale.x;
            _style.minHeight = windowScale.y;
        } else {
            _style.width = windowScale.x;
            _style.height = windowScale.y;
        }
    }
    if (coords) {
        _style.left = coords.x;
        _style.top = coords.y;
    }
    let sequencerScale = isHiding || !windowScale ? 1 : windowScale.x / 420.0;
    if (sequencerScale != 1) {
        sequencer.scale = sequencerScale;
    }

    let className = "pulled-sequencer";
    if (isCurrent) {
        className += " current";
    }
    if (isSelected) {
        className += " selected";
    }
    return (
        <div
          style={_style}
          onDrop={onDrop}
          onContextMenu={onContextMenu}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          ref={ref} className={className}>
          <div className="pulled-header">
            <div className="header-name">Sequencer</div>
            <div
              onClick={() => setStarred(!isStarred)}
              className={isStarred ? "starred star": "star"}>
              <span>&#9734;</span>
            </div>
            <div onClick={() => sequencer.dispose()} className="delete-button">
              x
            </div>
          </div>
          <div
            style={elements && sequencerScale && !isHiding ? {
                width: _style.width / sequencerScale,
                maxHeight: _style.height / (sequencerScale),
                transform: 'scale(' + sequencerScale*0.95 + ')'} : {}}
            ref={scrollRef} className="inner-sequencer">
            {sequencer.tracks.length === 0 &&
             <div className="drop-here">Drop Tracks Here</div>}
            {sequencer.tracks.map(
                track =>
                    <MiniTrackComponent
                      onTrackRendered={onTrackRendered}
                      sequencerScale={sequencerScale}
                      key={track.id}
                      setIsHiding={setIsHiding}
                      isHiding={isHiding}
                      key={track.id}
                      scrollContainer={scrollContainer}
                      track={track}/>
            )}
          </div>
          <div
            onMouseDown={(e) => startResizing(NW, e)}
            className="top-left-edge"/>
          <div
            onMouseDown={(e) => startResizing(NE, e)}
            className="top-right-edge"/>
          <div
            onMouseDown={(e) => startResizing(SW, e)}
            className="bottom-left-edge"/>
          <div
            onMouseDown={(e) => startResizing(SE, e)}
            className="bottom-right-edge"/>
          {sequencer.tracks.map(
              (track, i) =>
                  <OutputNode
                    sequencerScale={sequencerScale}
                    isMidi={true}
                    trackPositions={trackPositions}
                    number={i}
                    containerRef={scrollContainer}
                    sequencerEngine={sequencer.sequencerEngine}
                    scale={scale}
                    node={track}/>)}
        </div>
    );
};

const mapStateToProps = (state, props) => {
  return {
      coords: state.windows.windowPositions[props.sequencer.id],
      isSelected: state.windows.selectedElements[props.sequencer.id],
      tracks: props.sequencer.tracks,
      numTracks: props.sequencer.tracks.length,
      isStarred: state.effects.effectStarred[props.sequencer.id]
  };
};

const mapDispatchToProps = (dispatch, props) => {
  return {
      setStarred: s => dispatch(setEffectStarred(props.sequencer.id, s)),
      setTrackDragging: x => dispatch(setTrackDragging(x)),
      setSampleDragging: x => dispatch(setSampleDragging(x)),
      setSampleTagDragging: x => dispatch(setSampleTagDragging(x)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PulledSequencer);
